import { useLazyQuery, useMutation } from "@apollo/client";
import { AlertCircle, CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { AppointmentTimeSelect } from "@/components/ui/compact-time-select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  UPDATE_APPOINTMENT_MUTATION,
  VALIDATE_BOOKING_CONSTRAINTS_QUERY,
} from "@/graphql/appointments";
import { useSalonStore } from "@/lib/store/salon";
import { timezoneUtils } from "@/lib/timezone";
import { cn } from "@/lib/utils";

interface EditTimeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  currentStartTime: string;
  currentDuration: number;
}

export function EditTimeDialog({
  open,
  onOpenChange,
  appointmentId,
  currentStartTime,
  currentDuration,
}: EditTimeDialogProps) {
  const { currentSalon } = useSalonStore();
  const { t: tForms } = useTranslation("forms");
  const { t: tErrors } = useTranslation("errors");

  // Initialize with current appointment time, projected to salon TZ so the
  // dialog shows the appointment's salon-local date/time rather than the UTC
  // instant returned by the server's ISO-with-offset string.
  const currentDateTime = timezoneUtils.fromISO(currentStartTime);
  const [date, setDate] = useState<DateTime>(currentDateTime);
  const [time, setTime] = useState<string>(currentDateTime.toFormat("HH:mm"));
  const [validationError, setValidationError] = useState<string>("");
  const [isValidating, setIsValidating] = useState(false);

  const [updateAppointment, { loading: isUpdating }] = useMutation(UPDATE_APPOINTMENT_MUTATION);
  const [validateConstraints] = useLazyQuery(VALIDATE_BOOKING_CONSTRAINTS_QUERY);

  // Get start and end times for validation
  const getStartEndTimes = useCallback(() => {
    if (!date || !time) return null;

    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    const startTime = date.set({
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    });

    const endTime = startTime.plus({ minutes: currentDuration });

    return { startTime, endTime };
  }, [date, time, currentDuration]);

  // Validate booking constraints
  const validateBooking = useCallback(async (): Promise<boolean> => {
    if (!currentSalon?.id) return false;

    const times = getStartEndTimes();
    if (!times) return false;

    setIsValidating(true);
    setValidationError("");

    try {
      // perform validation request
      const startTimeString = times.startTime.toISO() ?? "";
      const endTimeString = times.endTime.toISO() ?? "";

      const result = await validateConstraints({
        variables: {
          input: {
            salonId: currentSalon.id,
            startTime: startTimeString,
            endTime: endTimeString,
          },
        },
      });

      // Check for GraphQL errors
      if (result.error || result.errors) {
        setValidationError(tErrors("appointments.failedValidateBookingConstraints"));
        return false;
      }

      const validation = result.data?.validateBookingConstraints;

      if (!validation?.isValid) {
        const errorMessage = validation?.errors?.[0] || tForms("descriptions.overrideWarning");
        setValidationError(errorMessage);
        return false;
      }

      return true;
    } catch (_error) {
      setValidationError(tErrors("appointments.failedValidateBookingConstraints"));
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [currentSalon?.id, getStartEndTimes, validateConstraints, tErrors, tForms]);

  // Initialize state on dialog open
  useEffect(() => {
    if (open) {
      const currentDateTime = timezoneUtils.fromISO(currentStartTime);
      setDate(currentDateTime);
      setTime(currentDateTime.toFormat("HH:mm"));
      setValidationError("");
    }
  }, [open, currentStartTime]);

  // Validate whenever date or time changes
  useEffect(() => {
    if (open && currentSalon?.id && date && time) {
      validateBooking();
    }
  }, [date, time, open, currentSalon?.id, validateBooking]);

  // Handle date selection from calendar.
  //
  // shadcn Calendar returns a JS Date pinned to midnight in the *browser's*
  // local timezone. `DateTime.fromJSDate` would preserve that UTC instant and
  // shift it into the salon timezone (Settings.defaultZone), so a user in a
  // tz east of the salon would silently end up one day earlier. Build the
  // DateTime from year/month/day components instead so the salon-tz date
  // matches the one the user clicked.
  const handleDateChange = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const newDate = DateTime.fromObject({
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        day: selectedDate.getDate(),
      });
      setDate(newDate);
    }
  };

  // Handle time change
  const handleTimeChange = (newTime: string) => {
    setTime(newTime);
  };

  // Check if time has changed from original
  const hasTimeChanged = (): boolean => {
    if (!date || !time) return false;

    const [hours, minutes] = time.split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;

    const selectedDateTime = date.set({
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    });

    const originalDateTime = timezoneUtils.fromISO(currentStartTime);

    return !selectedDateTime.equals(originalDateTime);
  };

  // shadcn Calendar uses `new Date()` for "today", which is browser-local.
  // When the browser is east of the salon and has crossed midnight, the
  // wrong day gets highlighted. Build a JS Date from salon-tz y/m/d so the
  // "today" marker matches the salon's current day.
  const salonNow = DateTime.now();
  const salonTodayAsJSDate = new Date(salonNow.year, salonNow.month - 1, salonNow.day);

  // Check if date is disabled (past dates)
  const isDateDisabled = (checkDate: Date): boolean => {
    const jsDate = new Date(checkDate);
    const today = DateTime.now().startOf("day");
    const checkDateTime = DateTime.fromObject({
      year: jsDate.getFullYear(),
      month: jsDate.getMonth() + 1,
      day: jsDate.getDate(),
    }).startOf("day");
    return checkDateTime < today;
  };

  // Handle save with validation
  const handleSave = async () => {
    const times = getStartEndTimes();
    if (!times) {
      toast.error(tErrors("appointments.selectValidDateTime"));
      return;
    }

    // If no validation error is showing, validate first
    if (!validationError) {
      const isValid = await validateBooking();
      if (!isValid) {
        // Validation error is now set in state, user can see it and decide to save anyway
        return;
      }
    }

    // If we get here, either validation passed OR user is clicking save with error showing (override)
    try {
      const startTimeISO = times.startTime.toISO();
      if (!startTimeISO) {
        throw new Error(tErrors("forms.invalidTime"));
      }

      await updateAppointment({
        variables: {
          salonId: currentSalon?.id ?? "",
          appointmentId: appointmentId,
          input: {
            startTime: startTimeISO,
          },
        },
        refetchQueries: ["Appointment"],
        awaitRefetchQueries: true,
      });

      toast.success(tForms("actions.save"));
      onOpenChange(false);
    } catch (_error) {
      toast.error(tErrors("appointments.failedUpdateAppointmentTime"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{tForms("titles.editTime")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>{`${tForms("labels.appointmentDate")} *`}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal mt-1",
                      !date && "text-muted-foreground"
                    )}
                    disabled={isUpdating}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? (
                      date.toLocaleString(DateTime.DATE_FULL)
                    ) : (
                      <span>{tForms("placeholders.selectDate")}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-50" align="start">
                  <Calendar
                    mode="single"
                    selected={
                      date
                        ? // Build a JS Date at midnight in the *browser's* local tz with
                          // the same y/m/d as the salon-tz DateTime, so Calendar (which
                          // reads JS Date components in local tz) highlights the right day.
                          new Date(date.year, date.month - 1, date.day)
                        : undefined
                    }
                    today={salonTodayAsJSDate}
                    onSelect={handleDateChange}
                    initialFocus
                    disabled={isDateDisabled}
                    className="p-3"
                    classNames={{
                      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                      month: "space-y-4",
                      caption: "flex justify-center pt-1 relative items-center",
                      caption_label: "text-sm font-medium",
                      nav: "space-x-1 flex items-center",
                      nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex",
                      head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
                      row: "flex w-full mt-2",
                      cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                      day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors",
                      day_selected:
                        "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                      day_today: "bg-accent text-accent-foreground",
                      day_outside: "text-muted-foreground opacity-50",
                      day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
                      day_range_middle:
                        "aria-selected:bg-accent aria-selected:text-accent-foreground",
                      day_hidden: "invisible",
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="time">{`${tForms("labels.appointmentTime")} *`}</Label>
              <AppointmentTimeSelect
                value={time}
                onChange={handleTimeChange}
                disabled={isUpdating}
                className="mt-1 w-full"
                size="md"
              />
            </div>
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-900 dark:text-red-100">{validationError}</p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                    {tForms("descriptions.overrideWarning")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            {tForms("actions.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isValidating || isUpdating || !time || !date || !hasTimeChanged()}
          >
            {isValidating
              ? tForms("actions.validating")
              : isUpdating
                ? tForms("actions.saving")
                : tForms("actions.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
